import { useId, useRef, useState } from 'react';
import { decideSignup, type RejectionReason } from '../../core/signup';

/**
 * Idle -> Submitting -> Confirmed, with Invalid and Failed as the two ways back.
 * Confirmed is terminal and replaces the form.
 */
type FormState = 'idle' | 'invalid' | 'submitting' | 'confirmed' | 'failed';

export interface NotifyFormProps {
  /** True while the feature gate is still resolving — inert, but not hidden. */
  disabled?: boolean;
  onSubmitAttempt?: () => void;
  onConfirmed?: () => void;
  onRejected?: (reason: string) => void;
  onFailed?: (status: number) => void;
}

const REJECTION_COPY: Record<RejectionReason, string> = {
  empty: 'Enter an email address first.',
  malformed: "That does not look like an email address — check it and try again.",
  too_long: 'That address is too long to be real. Check it and try again.',
};

const NETWORK_COPY = 'Something went wrong on our end. Try again in a moment.';

/**
 * Exported so tests can find the button without hardcoding its wording. The label is
 * marketing copy and will keep changing; the button's existence will not.
 */
export const SUBMIT_LABEL = 'Count me in';
export const SUBMIT_PENDING_LABEL = 'Sending…';

export default function NotifyForm({
  disabled = false,
  onSubmitAttempt,
  onConfirmed,
  onRejected,
  onFailed,
}: NotifyFormProps) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<FormState>('idle');
  const [message, setMessage] = useState('');

  const inputId = useId();
  const messageId = useId();
  const confirmationRef = useRef<HTMLDivElement>(null);

  function reject(reason: RejectionReason) {
    setState('invalid');
    setMessage(REJECTION_COPY[reason]);
    onRejected?.(reason);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (state === 'submitting' || disabled) return;

    // The same core the Worker uses, so "malformed" means one thing across both sides
    // by construction rather than by two implementations happening to agree.
    const decision = decideSignup(email, new Date().toISOString());
    if (!decision.ok) {
      reject(decision.reason);
      return;
    }

    onSubmitAttempt?.();
    setState('submitting');
    setMessage('');

    let response: Response;
    try {
      response = await fetch('/api/notify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: decision.record.email }),
      });
    } catch {
      // The typed address stays in the field. Asking a teacher to retype an email
      // after a network blip is the fastest way to lose the lead.
      setState('failed');
      setMessage(NETWORK_COPY);
      onFailed?.(0);
      return;
    }

    if (response.ok) {
      setState('confirmed');
      onConfirmed?.();
      // Focus follows the content that replaced the form, or a keyboard user is left
      // pointing at an element that no longer exists.
      requestAnimationFrame(() => confirmationRef.current?.focus());
      return;
    }

    if (response.status === 400) {
      const body = (await response.json().catch(() => ({}))) as { reason?: RejectionReason };
      reject(body.reason ?? 'malformed');
      return;
    }

    setState('failed');
    setMessage(NETWORK_COPY);
    onFailed?.(response.status);
  }

  if (state === 'confirmed') {
    return (
      <div className="confirmation" ref={confirmationRef} tabIndex={-1}>
        <h2>You are on the list.</h2>
        <p>You will see 240 Labs before anyone outside 240 does. Thank you.</p>
      </div>
    );
  }

  const invalid = state === 'invalid';

  return (
    <>
      <form className="form" onSubmit={handleSubmit} noValidate>
        <label className="visually-hidden" htmlFor={inputId}>
          Email address
        </label>
        <input
          id={inputId}
          className="field"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@school.edu"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={invalid}
          aria-describedby={messageId}
          disabled={disabled}
        />
        <button className="submit" type="submit" disabled={disabled || state === 'submitting'}>
          {state === 'submitting' ? (
            SUBMIT_PENDING_LABEL
          ) : (
            <>
              {SUBMIT_LABEL}
              {/* aria-hidden keeps the accessible name "Count me in" rather than
                  "Count me in right arrow". */}
              <span className="submit-arrow" aria-hidden="true">
                →
              </span>
            </>
          )}
        </button>
      </form>

      <p className="message" id={messageId} role="status" aria-live="polite" data-tone={message ? 'error' : undefined}>
        {message}
      </p>

    </>
  );
}
